import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { departure, occurrenceStart, wallTimeInZone } from '@kraftwerk/shared';
import { db } from '../db/local.js';
import { cancelOccurrence, missedTraining, upcomingTraining } from '../db/schedule.js';
import type { MissedTraining, UpcomingTraining } from '../db/schedule.js';
import { readProfile, updateTrainingReminder } from '../db/profile.js';
import { useTimeZone, useToday } from '../hooks/useToday.js';
import { formatDay } from '../format.js';
import { MoveTraining } from './training/MoveTraining.js';

/**
 * Training heute — nur wenn eins ansteht und noch nicht erledigt ist.
 *
 * „Heute" zeigt, was jetzt dran ist (CLAUDE.md): kein Wochenplan, nur der
 * eine Termin, wann losgehen und der Weg ins Training. Seit 18.10.2026 auch:
 * verschieben oder auslassen direkt hier, und ein verpasster Termin fragt
 * einmal nach — nachholen oder auslassen. Der Plan-Tag geht dabei nie
 * verloren, er wartet aufs nächste Training (`assignPlanDays`).
 */
export function TodayTraining({
  highlight,
  onOpenTraining,
}: {
  highlight: boolean;
  onOpenTraining: () => void;
}): React.JSX.Element | null {
  const timeZone = useTimeZone();
  const today = useToday();
  const data = useLiveQuery(async () => {
    if (!timeZone) return null;
    const [list, missed, profile] = await Promise.all([
      upcomingTraining(timeZone, 1, db),
      missedTraining(timeZone, db),
      readProfile(db),
    ]);
    return { list, missed, profile };
  }, [timeZone]);
  if (!data || !timeZone || !today) return null;

  const next: UpcomingTraining | undefined = data.list.find(
    (entry) => entry.status === 'planned' && entry.sessionId === null && !entry.missed,
  );

  return (
    <>
      {data.missed.map((entry) => (
        <MissedTile
          key={`${entry.slotId ?? entry.dayId}-${entry.date}`}
          entry={entry}
          today={today}
        />
      ))}
      {next ? (
        <NextTile
          entry={next}
          today={today}
          timeZone={timeZone}
          travelMin={data.profile.travelMin}
          reminderOn={data.profile.trainingReminder}
          highlight={highlight}
          onOpenTraining={onOpenTraining}
        />
      ) : null}
    </>
  );
}

function NextTile({
  entry,
  today,
  timeZone,
  travelMin,
  reminderOn,
  highlight,
  onOpenTraining,
}: {
  entry: UpcomingTraining;
  today: string;
  timeZone: string;
  travelMin: number;
  reminderOn: boolean;
  highlight: boolean;
  onOpenTraining: () => void;
}): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [moving, setMoving] = useState(false);
  const start = occurrenceStart(entry, timeZone);
  const { leaveAt } = departure(start, travelMin, 0);
  const day = formatDay(entry.date, 'short', i18n.language);

  return (
    <section
      aria-labelledby="today-training"
      className={`kw-tile flex flex-col gap-2 ${highlight ? 'border-accent' : ''}`}
    >
      <h2 id="today-training" className="kw-kicker">
        {t('schedule.today')}
      </h2>
      <p className="text-base font-semibold">
        {t('schedule.todayLine', {
          time: entry.time,
          name: entry.planDayName ?? t('schedule.noPlanDay'),
        })}
      </p>
      {travelMin > 0 ? (
        <p className="text-sm text-ink-muted">
          {t('schedule.leaveAt', { time: wallTimeInZone(leaveAt, timeZone) })}
        </p>
      ) : null}
      <button type="button" onClick={onOpenTraining} className="kw-btn kw-btn-small">
        {t('schedule.toTraining')}
      </button>
      <div className="flex gap-2">
        <button
          type="button"
          aria-expanded={moving}
          aria-label={t('schedule.moveNamed', { day })}
          onClick={() => setMoving((value) => !value)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('schedule.move')}
        </button>
        <button
          type="button"
          aria-label={t('schedule.cancelNamed', { day })}
          onClick={() => void cancelOccurrence(entry, db)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('schedule.cancel')}
        </button>
      </div>
      {moving ? <MoveTraining entry={entry} today={today} onDone={() => setMoving(false)} /> : null}
      {!reminderOn ? (
        <button
          type="button"
          onClick={() => void updateTrainingReminder({ trainingReminder: true }, db)}
          className="text-left text-xs text-ink-muted underline underline-offset-2"
        >
          {t('schedule.reminderEnable')}
        </button>
      ) : null}
    </section>
  );
}

function MissedTile({ entry, today }: { entry: MissedTraining; today: string }): React.JSX.Element {
  const { t, i18n } = useTranslation();
  const [moving, setMoving] = useState(false);
  const day = formatDay(entry.date, 'short', i18n.language);

  return (
    <section className="kw-tile flex flex-col gap-2 border-warn">
      <h2 className="kw-kicker text-warn">{t('schedule.missedHeading')}</h2>
      <p className="text-base font-semibold">
        {t('schedule.missedLine', { day, time: entry.time })}
      </p>
      <p className="text-sm text-ink-muted">
        {entry.waitingPlanDayName
          ? t('schedule.missedWaiting', { name: entry.waitingPlanDayName })
          : t('schedule.missedWaitingNoPlan')}
      </p>
      <div className="flex gap-2">
        <button
          type="button"
          aria-expanded={moving}
          aria-label={t('schedule.catchUpNamed', { day })}
          onClick={() => setMoving((value) => !value)}
          className="kw-btn kw-btn-small flex-1"
        >
          {t('schedule.catchUp')}
        </button>
        <button
          type="button"
          aria-label={t('schedule.cancelNamed', { day })}
          onClick={() => void cancelOccurrence(entry, db)}
          className="kw-btn kw-btn-quiet kw-btn-small flex-1"
        >
          {t('schedule.cancel')}
        </button>
      </div>
      {moving ? <MoveTraining entry={entry} today={today} onDone={() => setMoving(false)} /> : null}
    </section>
  );
}
